import { Chunk, Option, Order, Schema } from 'effect';
import { BooleanFromString } from '@vs-shop/schemas/primitive/boolean';

import { FacetKey, facetKeyId } from '@vs-shop/engine/capability/productListing/facetKey';
import {
  Facet,
  type FacetOption,
  type FacetSet,
} from '@vs-shop/engine/capability/productListing/facetSet';
import { AttributeCode } from '@vs-shop/engine/capability/productListing/identity';
import type { ListingSelection } from '@vs-shop/engine/capability/productListing/listingSelection';
import type {
  PriceRangeNullable,
  ProductAttributeSortedSet,
} from '@vs-shop/schemas/product/filter/backend/productFilters';
import type { SearchFacets } from '@vs-shop/schemas/search/searchFacets';
import { warehouseConfigArray } from '@vs-shop/schemas/tenant/warehouses';

import { isCzChannel } from '../../config/channel';
import { getMemoizedIntlShapeWithDefaultLocale } from '../../utils/intl';

const booleanFromKey = Schema.decodeUnknownOption(BooleanFromString);

const booleanLabel = (value: string): string => {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  return value === 'true'
    ? intl.formatMessage({ id: 'component.server.filter.true', defaultMessage: 'Ano' })
    : intl.formatMessage({ id: 'component.server.filter.false', defaultMessage: 'Ne' });
};

const priceKey = FacetKey.make({ _tag: 'Price' });
const brandKey = FacetKey.make({ _tag: 'Brand' });
const taxonKey = FacetKey.make({ _tag: 'Taxon' });
const stockKey = FacetKey.make({ _tag: 'StockLocation' });

const selectedValues = (selection: ListingSelection, key: Facet['key']): ReadonlySet<string> =>
  new Set(selection.terms.find((term) => facetKeyId(term.key) === facetKeyId(key))?.values ?? []);

const selectedRange = (selection: ListingSelection, key: Facet['key']) => {
  const range = selection.ranges.find((entry) => facetKeyId(entry.key) === facetKeyId(key));
  return { min: range?.min ?? null, max: range?.max ?? null };
};

const terms = (
  key: Facet['key'],
  name: string | null,
  unit: string | null,
  values: ReadonlyArray<{
    value: string;
    label: string;
    count: number | null;
    searchText?: string;
  }>,
  selection: ListingSelection
): Extract<Facet, { readonly _tag: 'Terms' }> | null => {
  const chosen = selectedValues(selection, key);
  const offered = new Set(values.map((value) => value.value));
  const options: ReadonlyArray<FacetOption> = [
    ...values,
    ...[...chosen]
      .filter((value) => !offered.has(value))
      .map((value) => ({ value, label: value, count: null })),
  ].map((value) => {
    const option: FacetOption = {
      value: value.value,
      label: value.label,
      count: value.count,
      selected: chosen.has(value.value),
    };
    return 'searchText' in value ? Object.assign(option, { searchText: value.searchText }) : option;
  });
  if (options.length === 0) return null;
  return { _tag: 'Terms', key, name, unit, options };
};

const range = (
  key: Facet['key'],
  name: string | null,
  unit: string | null,
  min: number | undefined,
  max: number | undefined,
  values: ReadonlyArray<number>,
  selection: ListingSelection
): Extract<Facet, { readonly _tag: 'Range' }> | null =>
  min === undefined || max === undefined || min > max
    ? null
    : {
        _tag: 'Range',
        key,
        name,
        unit,
        bounds: { min, max },
        values,
        selected: selectedRange(selection, key),
      };

const numericAttributeRange = (
  key: Facet['key'],
  name: string | null,
  unit: string | null,
  values: ReadonlyArray<number>,
  selection: ListingSelection
): Extract<Facet, { readonly _tag: 'Range' }> | null => {
  const numbers = values.toSorted((left, right) => left - right);
  return range(key, name, unit, numbers[0], numbers.at(-1), numbers, selection);
};

const stockFacet = (selection: ListingSelection): Facet | null =>
  isCzChannel()
    ? terms(
        stockKey,
        null,
        null,
        warehouseConfigArray.map(({ code, name }) => ({ value: code, label: name, count: null })),
        selection
      )
    : null;

const categoryAttributeFacets = (
  attributes: ProductAttributeSortedSet,
  selection: ListingSelection
): ReadonlyArray<Facet> =>
  attributes.flatMap((attribute) => {
    const key = FacetKey.make({ _tag: 'Attribute', code: AttributeCode.make(attribute.code) });
    const values = Chunk.toReadonlyArray(attribute.attributeValues);

    if (attribute.type === 'checkbox') {
      if (values.length === 0) return [];
      const facet = terms(
        key,
        attribute.name,
        attribute.unit,
        ['true', 'false'].map((value) => ({ value, label: booleanLabel(value), count: null })),
        selection
      );
      return facet === null ? [] : [Facet.make({ ...facet, valueType: 'boolean' })];
    }

    if (attribute.type === 'integer' || attribute.type === 'float') {
      const numbers = values.filter((value): value is number => typeof value === 'number');
      const facet = numericAttributeRange(key, attribute.name, attribute.unit, numbers, selection);
      return facet === null ? [] : [Facet.make({ ...facet, valueType: attribute.type })];
    }

    const facet = terms(
      key,
      attribute.name,
      attribute.unit,
      values.map((value) => {
        if (typeof value === 'object') {
          return {
            value: String(value.value),
            label: `${value.label}${attribute.unit === null ? '' : ` ${attribute.unit}`}`,
            count: null,
          };
        }
        const label = typeof value === 'boolean' ? booleanLabel(String(value)) : String(value);
        return {
          value: String(value),
          label: `${label}${attribute.unit === null ? '' : ` ${attribute.unit}`}`,
          count: null,
        };
      }),
      selection
    );
    return facet === null ? [] : [facet];
  });

export type CategoryFacetSource = {
  readonly attributes: ProductAttributeSortedSet;
  readonly priceRange: PriceRangeNullable;
  readonly brands?: ReadonlyArray<{
    readonly id: string;
    readonly name: string | null;
    readonly code?: string;
  }>;
};

/** Projects category or brand metadata into neutral listing facets. */
export const toCategoryFacetSet = (
  source: CategoryFacetSource,
  selection: ListingSelection,
  total: number
): FacetSet => {
  const price = range(
    priceKey,
    null,
    null,
    source.priceRange?.min,
    source.priceRange?.max,
    [],
    selection
  );
  const brands = terms(
    brandKey,
    null,
    null,
    (source.brands ?? [])
      .toSorted((left, right) => Order.String(left.name ?? '', right.name ?? ''))
      .map((brand) => ({
        value: brand.id,
        label: brand.name ?? brand.id,
        count: null,
        searchText: brand.code ?? brand.name ?? brand.id,
      })),
    selection
  );

  return {
    facets: [
      price,
      brands,
      stockFacet(selection),
      ...categoryAttributeFacets(source.attributes, selection),
    ].filter((facet): facet is Facet => facet !== null),
    total,
  };
};

/** Projects the search facets response into neutral listing facets. */
export const toSearchFacetSet = (response: SearchFacets, selection: ListingSelection): FacetSet => {
  const bucketFacet = (
    key: Facet['key'],
    buckets: ReadonlyArray<{ key: string; label: string; count: number }>
  ) =>
    terms(
      key,
      null,
      null,
      buckets.map((bucket) => ({
        value: bucket.key,
        label: bucket.label,
        count: bucket.count,
      })),
      selection
    );

  const price = range(
    priceKey,
    null,
    null,
    response.price?.min,
    response.price?.max,
    [],
    selection
  );
  const attributes = response.attributes.flatMap((attribute) => {
    const key = FacetKey.make({ _tag: 'Attribute', code: AttributeCode.make(attribute.code) });
    const facet = terms(
      key,
      attribute.name,
      attribute.unit,
      attribute.values.flatMap((value) => {
        if (attribute.type === 'checkbox') {
          return Option.match(booleanFromKey(value.key), {
            onNone: () => [],
            onSome: (boolean) => [
              {
                value: String(boolean),
                label: booleanLabel(String(boolean)),
                count: value.count,
              },
            ],
          });
        }
        return [
          {
            value: value.key,
            label: `${value.label ?? value.key}${attribute.unit === null ? '' : ` ${attribute.unit}`}`,
            count: value.count,
          },
        ];
      }),
      selection
    );
    return facet === null
      ? []
      : [attribute.type === 'checkbox' ? Facet.make({ ...facet, valueType: 'boolean' }) : facet];
  });

  return {
    facets: [
      price,
      bucketFacet(taxonKey, response.taxons),
      bucketFacet(brandKey, response.brands),
      stockFacet(selection),
      ...attributes,
    ].filter((facet): facet is Facet => facet !== null),
    total: response.totalItems,
  };
};
